import {useState} from "react";
import {Button} from "../../../basic/button";
import {Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle} from "../../../basic/dialog";
import {toast} from "sonner";
import {useTranslationWithKeys} from "../../../../hooks/useTranslationWithKeys";
import {useErrorHandler} from "../../../../hooks/useErrorHandler";
import {TeamInfo} from "./TeamInfo";
import {TeamBillingInfo} from "./TeamBillingInfo";
import {TeamSettingsSection} from "./TeamSettingsSection";
import {AddLicenseForm} from "./AddLicenseForm";
import {AddUserForm} from "./AddUserForm";
import {TeamLicenses} from "./TeamLicenses";
import {TeamMembersList} from "./TeamMembersList";
import {TeamArchivedMembersList} from "./TeamArchivedMembersList";
import {RemoveTeamMemberAlertDialog} from "./RemoveTeamMemberAlertDialog";
import {CollapsibleSection} from "../../../shared/CollapsibleSection";
import {Separator} from "../../../basic/separator";
import {Tabs, TabsContent, TabsList, TabsTrigger} from "../../../basic/tabs";
import {Key, Users} from "lucide-react";
import {
    useAddTeamMemberLicenseMutation,
    useAssignLicenseToTeamMutation,
    useAssignUserToTeamMutation,
    useRemoveTeamMemberByAdminMutation,
    useRemoveTeamMemberLicenseMutation,
    VwpTeam
} from "../../../../store/generated/api";
import {getDisplayName} from "../../../../utils/nameUtils.ts";
import {getTeamDisplayName} from "../../../../utils/teamUtils";

interface TeamDetailsDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    selectedTeam: VwpTeam | null;
    isLoading?: boolean;
    error?: any;
    onTeamUpdate?: () => void;
}

export function TeamDetailsDialog({
                                      open,
                                      onOpenChange,
                                      selectedTeam,
                                      isLoading = false,
                                      error,
                                      onTeamUpdate
                                  }: TeamDetailsDialogProps) {
    const {t} = useTranslationWithKeys();
    const {showErrorToast, handleApiError} = useErrorHandler();
    const [newLicenseId, setNewLicenseId] = useState("");
    const [newUserId, setNewUserId] = useState("");
    const [managingMemberId, setManagingMemberId] = useState<number | null>(null);
    const [memberTab, setMemberTab] = useState("active");
    const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
    const [selectedMember, setSelectedMember] = useState<{
        email: string,
        name: string,
        licenses: string[]
    } | null>(null);

    const handleClose = () => {
        onOpenChange(false);
        setNewLicenseId("");
        setNewUserId("");
        setManagingMemberId(null);
        setMemberTab("active");
        setRemoveDialogOpen(false);
        setSelectedMember(null);
    };

    const [assignLicenseToTeam, {isLoading: isAssigningTeamLicense}] = useAssignLicenseToTeamMutation();
    const [assignUserToTeam, {isLoading: isAssigningUser}] = useAssignUserToTeamMutation();
    const [removeTeamMemberByAdmin, {isLoading: isRemovingMember}] = useRemoveTeamMemberByAdminMutation();

    const handleAddLicense = async () => {
        if (!newLicenseId.trim()) {
            showErrorToast({message: t('admin.teams.teamManagement.errors.enterLicenseId')});
            return;
        }

        try {
            await assignLicenseToTeam({
                licenseId: newLicenseId,
                vwpAssignLicenseToTeamRequest: {
                    teamId: selectedTeam.id
                }
            }).unwrap();

            toast.success(t('admin.teams.teamManagement.licenseAdded', {
                licenseId: newLicenseId,
                teamName: getTeamDisplayName({
                    teamName: selectedTeam.name,
                    firstName: selectedTeam.adminName?.split(' ')[0],
                    lastName: selectedTeam.adminName?.split(' ').slice(1).join(' ')
                }) ?? t('common.noTeam')
            }));
            setNewLicenseId("");
        } catch (error) {
            handleApiError(error, {fallbackMessage: t('admin.teams.teamManagement.licenseAddError')});
        }
    };

    const handleAddUser = async () => {
        if (!newUserId || newUserId === "none") {
            showErrorToast({message: t('admin.teams.teamManagement.errors.selectUser')});
            return;
        }

        try {
            const response = await assignUserToTeam({
                userId: parseInt(newUserId),
                vwpUserTeamChangeRequest: {
                    teamId: selectedTeam.id
                }
            }).unwrap();

            toast.success(t('admin.teams.teamManagement.userAdded', {
                userName: getDisplayName({
                    firstName: response.firstName,
                    lastName: response.lastName,
                    email: response.userEmail
                }),
                teamName: getTeamDisplayName({
                    teamName: selectedTeam.name,
                    firstName: selectedTeam.adminName?.split(' ')[0],
                    lastName: selectedTeam.adminName?.split(' ').slice(1).join(' ')
                }) ?? t('common.noTeam')
            }));
            setNewUserId("");
        } catch (error) {
            handleApiError(error, {fallbackMessage: t('admin.teams.teamManagement.userAddError')});
        }
    };

    const [addTeamMemberLicense, {isLoading: isAssigningLicense}] = useAddTeamMemberLicenseMutation();
    const [removeTeamMemberLicense, {isLoading: isRemovingLicense}] = useRemoveTeamMemberLicenseMutation();

    const handleRemoveLicense = async (licenseId: string, licenseName: string, memberEmail: string, memberName: string) => {
        try {
            await removeTeamMemberLicense({
                teamId: selectedTeam.id,
                memberEmail: memberEmail,
                licenseId: licenseId
            }).unwrap();

            toast.success(t('admin.teams.teamManagement.licenseRemoved', {
                license: licenseName,
                member: memberName
            }));
        } catch (error) {
            handleApiError(error, {fallbackMessage: t('admin.teams.teamManagement.licenseRemoveError', {memberName})});
        }
    };

    const handleAssignLicense = async (licenseId: string, licenseName: string, memberEmail: string, memberName: string) => {
        try {
            await addTeamMemberLicense({
                teamId: selectedTeam.id,
                memberEmail: memberEmail,
                vwpAddLicenseAssignmentRequest: {
                    userEmail: memberEmail,
                    licenseId: licenseId
                }
            }).unwrap();

            toast.success(t('admin.teams.teamManagement.licenseAssigned', {
                license: licenseName,
                member: memberName
            }));
            setManagingMemberId(null);
        } catch (error) {
            handleApiError(error, {fallbackMessage: t('admin.teams.teamManagement.licenseAssignError')});
        }
    };

    const handleToggleManageMember = (memberId: number) => {
        setManagingMemberId(managingMemberId === memberId ? null : memberId);
    };

    const handleRemoveMember = (memberEmail: string, memberName: string) => {
        const member = teamMembers.find(m => m.email === memberEmail);
        setSelectedMember({
            email: memberEmail,
            name: memberName,
            licenses: member?.licenses?.map(l => l.name) || []
        });
        setRemoveDialogOpen(true);
    };

    const handleConfirmRemoveMember = async () => {
        if (!selectedMember || !selectedTeam) return;

        try {
            await removeTeamMemberByAdmin({
                teamId: selectedTeam.id,
                memberEmail: selectedMember.email
            }).unwrap();

            toast.success(t('admin.teams.teamManagement.memberRemoved', {
                memberName: getDisplayName(selectedMember),
                teamName: getTeamDisplayName({
                    teamName: selectedTeam.name,
                    firstName: selectedTeam.adminName?.split(' ')[0],
                    lastName: selectedTeam.adminName?.split(' ').slice(1).join(' ')
                }) ?? t('common.noTeam')
            }));
            setRemoveDialogOpen(false);
            setSelectedMember(null);
        } catch (error) {
            handleApiError(error, {
                fallbackMessage: t('admin.teams.teamManagement.errors.memberRemoveError', {
                memberName: getDisplayName(selectedMember)
                })
            });
        }
    };

    if (isLoading) {
        return (
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent className="w-[90vw] max-w-7xl max-h-[90vh] flex flex-col">
                    <DialogHeader>
                        <DialogTitle>{t('admin.teams.teamManagement.title')}</DialogTitle>
                        <DialogDescription>
                            {t('admin.teams.teamManagement.description')}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="flex items-center justify-center py-12">
                        <div className="text-muted-foreground">{t('common.loading')}</div>
                    </div>
                </DialogContent>
            </Dialog>
        );
    }

    if (error) {
        return (
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent className="w-[90vw] max-w-7xl max-h-[90vh] flex flex-col">
                    <DialogHeader>
                        <DialogTitle>{t('admin.teams.teamManagement.title')}</DialogTitle>
                        <DialogDescription>
                            {t('admin.teams.teamManagement.description')}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="flex items-center justify-center py-12">
                        <div className="text-destructive">
                            {error?.data?.message || t('admin.teams.error.loadingFailed')}
                        </div>
                    </div>
                    <DialogFooter className="flex-shrink-0">
                        <Button variant="outline" onClick={handleClose}>
                            {t('common.close')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        );
    }

    if (!selectedTeam) return null;

    const teamMembers = selectedTeam.teamMembers || [];
    const teamLicenses = selectedTeam.licenses || [];
    const availableLicenses = teamLicenses.filter(l => l.assigned < l.quantity);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="w-[90vw] max-w-7xl max-h-[90vh] flex flex-col">
                <DialogHeader>
                    <DialogTitle>{t('admin.teams.teamManagement.title')}</DialogTitle>
                    <DialogDescription>
                        {t('admin.teams.teamManagement.description')}
                    </DialogDescription>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto space-y-6 py-4">
                    <TeamInfo selectedTeam={selectedTeam}/>

                    <TeamSettingsSection selectedTeam={selectedTeam} onTeamUpdate={onTeamUpdate}/>

                    <TeamBillingInfo selectedTeam={selectedTeam} onTeamUpdate={onTeamUpdate}/>

                    <Separator className="my-4"/>

                    <CollapsibleSection
                        title="Team Licenses"
                        icon={<Key className="h-5 w-5"/>}
                        defaultOpen={true}
                        className="mb-4"
                    >
                        <AddLicenseForm
                            newLicenseId={newLicenseId}
                            setNewLicenseId={setNewLicenseId}
                            onAddLicense={handleAddLicense}
                            isLoading={isAssigningTeamLicense}
                        />
                        <TeamLicenses teamLicenses={teamLicenses}/>
                    </CollapsibleSection>

                    <Separator className="my-4"/>

                    <CollapsibleSection
                        title="Team Members"
                        icon={<Users className="h-5 w-5"/>}
                        defaultOpen={true}
                        className="mb-4"
                    >
                        <Tabs value={memberTab} onValueChange={setMemberTab} className="w-full">
                            <TabsList>
                                <TabsTrigger value="active">
                                    {t('team.active')}
                                </TabsTrigger>
                                <TabsTrigger value="archived">
                                    {t('team.archived')}
                                </TabsTrigger>
                            </TabsList>

                            <TabsContent value="active">
                                <AddUserForm
                                    newUserId={newUserId}
                                    setNewUserId={setNewUserId}
                                    onAddUser={handleAddUser}
                                    isLoading={isAssigningUser}
                                />
                                <TeamMembersList
                                    teamMembers={teamMembers}
                                    availableLicenses={availableLicenses}
                                    managingMemberId={managingMemberId}
                                    isAssigningLicense={isAssigningLicense}
                                    isRemovingLicense={isRemovingLicense}
                                    onToggleManageMember={handleToggleManageMember}
                                    onAssignLicense={handleAssignLicense}
                                    onRemoveLicense={handleRemoveLicense}
                                    onRemoveMember={handleRemoveMember}
                                />
                            </TabsContent>

                            <TabsContent value="archived">
                                <TeamArchivedMembersList teamId={selectedTeam.id}/>
                            </TabsContent>
                        </Tabs>
                    </CollapsibleSection>
                </div>

                <RemoveTeamMemberAlertDialog
                    open={removeDialogOpen}
                    onOpenChange={setRemoveDialogOpen}
                    onConfirm={handleConfirmRemoveMember}
                    memberName={getDisplayName(selectedMember)}
                    memberEmail={selectedMember?.email}
                    memberLicenses={selectedMember?.licenses}
                    isRemoving={isRemovingMember}
                />

                <DialogFooter className="flex-shrink-0">
                    <Button variant="outline" onClick={handleClose}>
                        {t('common.close')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}